import React, { useContext, useEffect, useRef, useState, ReactNode } from "react";
import { connect } from "react-redux";
import { Button, MCard, MGrid, MSpinner, MSplitter } from "components/Shared";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { RuleRecommendationListItem } from "interfaces/Mueller.Business.Models";
import {
  copyRulesetDefinition,
  createRulesetDefinition,
  getRulesRecommendationsList,
} from "requests/rulesRecommendations";
import { getSearchParameter } from "components/Shared/Functions";
import { withRouter } from "tools/routerCompat";
import type { RouteComponentProps } from "tools/routerCompat.d";
import RulesRecommendationsListDetails from "./RulesRecommendationsListDetails";
import useStateSorted from "hooks/useStateSorted";
import IconButton from "components/Shared/IconButton";
import { DATA_TYPE_DATETIME_WITHDOW } from "typeConstants/dataTypes";
import moment from "moment-timezone";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import {
  CreateRulesetDefinitionRequest,
  FormDefinitionCopyRequest,
  RulesetDefinition,
} from "interfaces/Phoenix.FormsDesigner.Models";
import { userHasRight } from "selectors/rights";
import { FORM_DESIGN_EDIT } from "typeConstants/rights";
import { HeaderDefinition } from "typeConstants/header/headerDefinitions";
import { clearHeader, updateHeader } from "actions/layout";

interface Props extends RouteComponentProps {
  location: RouteComponentProps["location"];
  history: any;
  canEditFormDesign: boolean;
  updateHeader: (headerDefinitions: HeaderDefinition) => void;
  clearHeader: () => void;
}

const RulesRecommendationsList = ({ location, history, canEditFormDesign, updateHeader, clearHeader }: Props) => {
  const mounted = useRef(true);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const [gridControlMethodsDownloadGrid, setGridControlMethodsDownloadGrid] = useState<{ func: () => void }>();
  const [_list, setList] = useStateSorted<RuleRecommendationListItem>(null, "name");
  let list = _list;
  const [nameFilter, setNameFilter] = useState(getSearchParameter(location));
  const [selectedItem, setSelectedItem] = useState<RuleRecommendationListItem | null>(null);
  const [isCreatingRule, setIsCreatingRule] = useState(false);

  const reloadRulesRecsList = () => {
    setList(null);
    getRulesRecommendationsList((list) => {
      setList(list);
      // update selected item with new data if it has changed
      if (selectedItem) setSelectedItem(list.find((r) => r.rowKey === selectedItem.rowKey));
    }, mounted);
  };

  useEffect(() => {
    reloadRulesRecsList();
    updateHeader({
      buttons: [
        {
          name: "Forms List",
          onClick: () => history.push("/phoenixforms-designer/forms-list"),
        },
      ],
    });
    return () => {
      mounted.current = false;
      clearHeader();
    };
  }, []);

  const createRule = (newRuleVals: CreateRulesetDefinitionRequest) => {
    createRulesetDefinition(
      newRuleVals,
      (rulesetDefinition: RulesetDefinition) =>
        history.push(`/phoenixforms-designer/rules-editor/${rulesetDefinition.formDefinitionId}`),
      mounted,
    );
  };

  const copyRule = (rulesetDefinitionId: string, copyRequest: FormDefinitionCopyRequest) => {
    copyRulesetDefinition(
      rulesetDefinitionId,
      copyRequest,
      (rulesetDefinition: RulesetDefinition) =>
        history.push(`/phoenixforms-designer/rules-editor/${rulesetDefinition.formDefinitionId}`),
      mounted,
    );
  };

  const onCopyRuleClicked = () => {
    if (!selectedItem?.rowKey) return;

    const copyRequest: FormDefinitionCopyRequest = {
      category: selectedItem.category,
      name: `${selectedItem.name} (Copy)`,
      description: selectedItem.description,
      destinationAccountId: undefined,
      pdfDefinitions: undefined,
      token: undefined,
    };

    dialog.showYesNo(
      null,
      <MCard
        title={`Copying rules/recs ${selectedItem.name}`}
        fields={[
          { key: "name", label: "Name", value: copyRequest.name },
          { key: "category", label: "Category", value: copyRequest.category },
          { key: "description", label: "Description", value: copyRequest.description },
        ]}
        onlyEditable
        onEditChanged={(kvp) => kvp.forEach(({ key, value }) => (copyRequest[key] = value))}
      />,
      () => {
        setIsCreatingRule(true);
        copyRule(selectedItem.rowKey, copyRequest);
      },
      dialog.hide,
      "Copy",
      "Cancel",
      { className: "dialog-widthfix new-form-dialog" },
    );
  };

  const onCreateRuleClicked = () => {
    const newRuleVals: CreateRulesetDefinitionRequest = {
      category: "",
      name: "",
      description: "",
      forcedID: null,
      token: undefined,
    };

    dialog.showYesNo(
      null,
      <MCard
        title="New Rules/Recs"
        fields={[
          { key: "name", label: "Name", value: newRuleVals.name },
          { key: "category", label: "Category", value: newRuleVals.category },
          { key: "description", label: "Description", value: newRuleVals.description },
        ]}
        onlyEditable
        onEditChanged={(kvp) => kvp.forEach(({ key, value }) => (newRuleVals[key] = value))}
      />,
      () => {
        setIsCreatingRule(true);
        createRule(newRuleVals);
      },
      dialog.hide,
      "Create",
      "Cancel",
      { className: "dialog-widthfix new-form-dialog" },
    );
  };

  const gridColDefs: MGridColumnDefinition<RuleRecommendationListItem>[] = [
    { key: "name", name: "Name", dataSource: "name", width: 280 },
    { key: "category", name: "Category", dataSource: "category", width: 140 },
    { key: "description", name: "Description", dataSource: "description" },
    { key: "modifiedDate", name: "Last Update", dataSource: "modifiedDate", dataType: DATA_TYPE_DATETIME_WITHDOW },
    { key: "modifiedBy", name: "Updated By", dataSource: "modifiedBy", width: 150 },
    { key: "publishDate", name: "Publish Date", dataSource: "publishDate", dataType: DATA_TYPE_DATETIME_WITHDOW },
    // { key: "inactiveDate", name: "Inactive Date", dataSource: "inactiveDate", dataType: DATA_TYPE_DATETIME_WITHDOW },
  ];

  let firstChild = <MSpinner />;

  if (nameFilter && list) {
    list = list.filter((f) => (f.name ?? "").toLocaleLowerCase().includes(nameFilter.toLowerCase()));
  }

  const selectedRow = list?.map((f, i) => (f.rowKey === selectedItem?.rowKey ? i : null)).filter((i) => i !== null);

  if (list && !isCreatingRule) {
    firstChild = (
      <>
        <div className="forms-list-header rules-recs-list">
          {canEditFormDesign && <IconButton icon="add" onClick={onCreateRuleClicked} tooltip="Create New Rules/Recs" />}
          <IconButton
            icon="cloud_download"
            onClick={() => gridControlMethodsDownloadGrid?.func()}
            disabled={!gridControlMethodsDownloadGrid}
          />
        </div>
        {nameFilter && (
          <div className="form-name-filter">
            <IconButton icon="close" onClick={() => setNameFilter(null)} />
            Filtering by name &quot;{nameFilter}&quot;
          </div>
        )}
        <MGrid
          isFetchingData={!list}
          columnDefinitions={gridColDefs}
          data={list}
          selectedRows={selectedRow}
          onRowClick={(i) => setSelectedItem(list[i])}
          getControlMethods={({ downloadGrid }) => setGridControlMethodsDownloadGrid({ func: downloadGrid })}
          downloadFileName={`RulesRecommendationsList-${moment().format("YYYY.MM.DD-HH.mm.ss")}`}
        />
      </>
    );
  }

  let secondChild: ReactNode = null;
  if (selectedItem) {
    secondChild = (
      <RulesRecommendationsListDetails
        item={selectedItem}
        onSaveFinished={reloadRulesRecsList}
        onCopyClick={onCopyRuleClicked}
      />
    );
  }

  return (
    <MSplitter
      className="designer-forms-list rules-recommendations-list"
      firstChild={firstChild}
      secondChildTitle={{
        title: selectedItem ? `Details for ${selectedItem.name}` : null,
        buttons: selectedItem ? [{ icon: "close", onClick: () => setSelectedItem(null) }] : [],
      }}
      secondChild={secondChild}
      firstChildInitialSize="40%"
    />
  );
};

const mapStateToProps = (state) => ({
  canEditFormDesign: userHasRight(FORM_DESIGN_EDIT)(state),
});

const mapDispatchToProps = (dispatch) => ({
  updateHeader: (headerDefinitions) => dispatch(updateHeader(headerDefinitions)),
  clearHeader: () => dispatch(clearHeader()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RulesRecommendationsList));
